import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Edit, Trash2, GripVertical, X, HelpCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFAQs = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/admin/login');
      return;
    }
    
    fetchFaqs();
  }, [navigate]);

  const fetchFaqs = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/admin/faqs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData({
      question: '',
      answer: '',
      order: faqs.length + 1,
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (faq) => {
    setEditingId(faq.id);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      order: faq.order,
      is_active: faq.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (!formData.question || !formData.answer) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API}/admin/faqs/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API}/admin/faqs`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('FAQ created');
      }
      setShowModal(false);
      fetchFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Failed to save FAQ');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/faqs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  const toggleActive = async (faq) => {
    const token = localStorage.getItem('token');
    
    try {
      await axios.put(`${API}/admin/faqs/${faq.id}`, 
        { is_active: !faq.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchFaqs();
    } catch (error) {
      console.error('Error updating FAQ:', error);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="font-playfair text-4xl font-bold mb-2">FAQs</h1>
          <p className="font-manrope text-sm text-gray-600">Manage frequently asked questions for homepage</p>
        </div>
        <button
          onClick={openCreateModal}
          data-testid="add-faq-btn"
          className="flex items-center gap-2 px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300"
        >
          <Plus className="w-4 h-4" />
          Add FAQ
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="font-manrope text-gray-600">Loading...</p>
        </div>
      ) : faqs.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-300">
          <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="font-manrope text-gray-600 mb-4">No FAQs yet</p>
          <button onClick={openCreateModal} className="font-manrope text-sm underline">
            Add your first FAQ
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div 
              key={faq.id} 
              className={`border p-4 ${faq.is_active ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-60'}`}
              data-testid={`faq-${faq.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="text-gray-400 pt-1">
                  <GripVertical className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="font-manrope font-semibold text-sm">{faq.question}</h3>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="font-mono text-xs text-gray-400">#{faq.order}</span>
                      <button
                        onClick={() => toggleActive(faq)}
                        className={`p-1 ${faq.is_active ? 'text-green-600' : 'text-gray-400'}`}
                        title={faq.is_active ? 'Active' : 'Inactive'}
                      >
                        {faq.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <p className="font-manrope text-sm text-gray-600 mb-3">{faq.answer}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(faq)}
                      className="p-1.5 border border-gray-200 hover:border-black transition-all text-gray-600 hover:text-black"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDelete(faq.id)}
                      className="p-1.5 border border-gray-200 hover:border-red-500 hover:text-red-500 transition-all text-gray-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="font-playfair text-xl font-bold">
                {editingId ? 'Edit FAQ' : 'Add FAQ'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Question *</label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => setFormData({...formData, question: e.target.value})}
                  placeholder="What happens if my article gets rejected?"
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
              </div>
              
              <div>
                <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Answer *</label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({...formData, answer: e.target.value})}
                  rows="4"
                  placeholder="Detailed answer..."
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                    min="0"
                    className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 cursor-pointer p-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="font-manrope text-sm">Active</span>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-200 font-manrope text-xs uppercase tracking-widest hover:border-black transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all"
                >
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminFAQs;
